import {
  WAREHOUSES_FETCH_STARTED,
  WAREHOUSES_FETCH_SUCCEEDED,
  WAREHOUSES_FETCH_FAILED,
  WAREHOUSES_CLEAR,
  WAREHOUSES_PAGINATED_FETCH_STARTED,
  WAREHOUSES_PAGINATED_FETCH_SUCCEEDED,
  WAREHOUSES_PAGINATED_FETCH_FAILED,
  WAREHOUSES_PAGINATED_CHANGE_PAGE,
  APPLIANCE_DEVICES_MERGE_TO_APPLIANCE
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { fetchAppliances } from './appliance/appliances';
import { getApplianceDevices } from './appliance/appliance-devices';
import { getRequestOptions, getURLParameter } from '#helper';

let abortController = new AbortController();

export const fetchWarehouses = (disableAbort, applianceStatus) => async dispatch => {
  try {
    dispatch({
      type: WAREHOUSES_FETCH_STARTED
    });

    let res;
    const searchParams = new URLSearchParams();

    if (applianceStatus) {
      searchParams.set('appliancePresence', applianceStatus);
    }

    const warehousesUrl = webPortalApi.buildApiUrl(`warehouse?${searchParams.toString()}`);

    if (!disableAbort) {
      abortController.abort();

      abortController = new AbortController();
      const { signal } = abortController;
      res = await fetch(warehousesUrl, { ...getRequestOptions(), signal });
    } else {
      res = await fetch(warehousesUrl, { ...getRequestOptions() });
    }

    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }
    const warehouses = await res.json();

    dispatch({
      type: WAREHOUSES_FETCH_SUCCEEDED,
      payload: warehouses
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: WAREHOUSES_FETCH_FAILED,
      error: e
    });
  }
};

export const fetchWarehousesAndAppliances = () =>
  async (dispatch, getState) => {
    try {
      abortController.abort();
      const {
        page,
        itemsPerPage,
        order,
        orderBy,
        filterBy
      } = getState().warehousesPaginated;
      const limit = itemsPerPage;
      const p = (getURLParameter('page') || page);
      const offset = (p - 1) * itemsPerPage;

      const params = new URLSearchParams({
        order,
        orderBy,
        limit,
        offset,
        pagination: 1
      });
      const filterKey = 'warehouse-manager';
      const warehouse = getState().listFilter?.[filterKey];

      const searchValue = warehouse?.search?.value;
      const projectValue = warehouse?.project?.value;
      const withAppliancesValue = warehouse?.whAppliance?.value?.[0]?.data?.value;
      const isEmptyAppliances = withAppliancesValue === 'empty';

      if (searchValue) {
        params.set('filter', searchValue);
        params.set('filterBy', filterBy);
      }

      if (projectValue) {
        const ids = projectValue.map(v => v.data.id).join(',');

        if (ids) {
          params.set('project', ids);
        }
      }

      if (withAppliancesValue) {
        if (withAppliancesValue !== 'all') {
          params.set('appliancePresence', withAppliancesValue);
        } else {
          params.delete('appliancePresence');
        }
      }

      const warehousesUrl = webPortalApi.buildApiUrl(`warehouse?${params.toString()}`);
      dispatch({
        type: WAREHOUSES_PAGINATED_FETCH_STARTED
      });
      abortController = new AbortController();
      const { signal } = abortController;
      const requestOptions = { ...getRequestOptions(), signal };
      const res = await fetch(warehousesUrl, requestOptions);
      const contentType = res.headers.get('content-type');

      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError('Response is not json');
      }
      if (!res.ok) {
        throw await res.json();
      }
      const { warehouses = [], last } = await res.json();
      if (warehouses.length && !isEmptyAppliances) {
        /*
         The `try` block ensures errors in fetching appliance data
         don't crash the function, allowing warehouse data to still load.
        */
        try {
          const ids = warehouses.map(w => w.id).join(',');
          const appliances = await dispatch(fetchAppliances(ids));
          const applianceIds = appliances?.map(a => a.id).join(',');

          if (applianceIds) {
            const applianceDevices = await getApplianceDevices(applianceIds, dispatch);

            const appliancesWithDeviceDetails = applianceDevices.map(applianceDevice =>
              ({
                ...applianceDevice,
                devices: applianceDevice.devices.map(device => ({
                  ...device,
                  groupLabel: applianceDevice.label,
                  groupAlertId: applianceDevice.projectAlertId
                }))
              }));

            dispatch({
              type: APPLIANCE_DEVICES_MERGE_TO_APPLIANCE,
              payload: appliancesWithDeviceDetails
            });
          }
        } catch (applianceError) {
          console.error('Failed to fetch appliances or appliance devices', applianceError);
        }
      }

      dispatch({
        type: WAREHOUSES_PAGINATED_FETCH_SUCCEEDED,
        payload: warehouses,
        totalCount: last
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }

      dispatch(snackBarError({ message: 'Error in request' }));

      dispatch({
        type: WAREHOUSES_PAGINATED_FETCH_FAILED,
        error: e
      });
    }
  };

export const changePage = (newPage, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: WAREHOUSES_PAGINATED_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchWarehousesAndAppliances());
  }
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: WAREHOUSES_CLEAR
  });
};
